import { faClock } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import type { BatchTraceabilityEvent } from '../../../models/Batch';

interface TraceabilityProps {
  events: BatchTraceabilityEvent[];
  isLoading: boolean;
}

const formatDateTime = (date: string): string => {
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const Traceability = ({ events, isLoading }: TraceabilityProps) => {
  const { t } = useTranslation('batchesModule');

  return (
    <section className="rounded-lg border border-card-border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faClock} className="w-4 h-4 text-complementary-text" />
          <h2 className="text-lg font-semibold">{t('detail.traceability.title')}</h2>
        </div>
        <span className="text-sm text-primary">
          {events.length === 0
            ? t('detail.traceability.movements_count_zero')
            : t('detail.traceability.movements_count', { count: events.length })}
        </span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <FontAwesomeIcon icon={faClock} className="w-10 h-10 text-complementary-text/50" />
          <p className="text-sm text-complementary-text">{t('detail.traceability.empty')}</p>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-card-border">
              <th className="text-left text-xs font-medium text-complementary-text uppercase py-2 px-2">
                {t('detail.traceability.event_type')}
              </th>
              <th className="text-left text-xs font-medium text-complementary-text uppercase py-2 px-2">
                {t('detail.traceability.direction')}
              </th>
              <th className="text-right text-xs font-medium text-complementary-text uppercase py-2 px-2">
                {t('detail.traceability.quantity')}
              </th>
              <th className="text-right text-xs font-medium text-complementary-text uppercase py-2 px-2">
                {t('detail.traceability.date')}
              </th>
            </tr>
          </thead>
          <tbody>
            {events.map((event, idx) => (
              <tr key={idx} className="border-b border-card-border/50">
                <td className="py-3 px-2 text-sm text-text">{event.event_type}</td>
                <td className="py-3 px-2">
                  <span className={`text-sm font-medium ${event.direction === 'in' ? 'text-success' : 'text-error'}`}>
                    {t(`detail.traceability.directions.${event.direction}`)}
                  </span>
                </td>
                <td className="text-right py-3 px-2 text-sm text-text font-medium">{event.quantity}</td>
                <td className="text-right py-3 px-2 text-sm text-complementary-text">
                  {formatDateTime(event.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};
